import React from 'react';
import { Container, Typography, Box, Grid, Button } from '@mui/material';
import { motion } from 'framer-motion';
import DownloadIcon from '@mui/icons-material/Download';

// Level 1–5: 1 = familiar, 3 = proficient, 5 = expert
const skills = [
  {
    category: 'Languages',
    items: [
      { name: 'Java',               level: 5 },
      { name: 'HTML / CSS',         level: 5 },
      { name: 'JavaScript',         level: 4 },
      { name: 'TypeScript',         level: 4 },
      { name: 'C#',                 level: 3 },
      { name: 'Python',             level: 3 },
      { name: 'Kotlin',             level: 2 },
      { name: 'C',                  level: 2 },
      { name: 'R',                  level: 1 },
      { name: 'Erlang',             level: 1 },
    ],
  },
  {
    category: 'Frameworks',
    items: [
      { name: 'Spigot API',         level: 5 },
      { name: 'React',              level: 4 },
      { name: 'Node.js',            level: 4 },
      { name: 'Express',            level: 3 },
      { name: 'Flask',              level: 3 },
      { name: 'RESTful APIs',       level: 3 },
      { name: 'SQL / ER Diagrams',  level: 3 },
    ],
  },
  {
    category: 'Tools & Platforms',
    items: [
      { name: 'Git',                level: 4 },
      { name: 'JetBrains IDEs',     level: 4 },
      { name: 'Azure',              level: 3 },
      { name: 'Docker',             level: 3 },
      { name: 'MongoDB',            level: 3 },
      { name: 'PostgreSQL',         level: 3 },
      { name: 'VirtualBox',         level: 3 },
      { name: 'Linux',              level: 2 },
      { name: 'FileZilla',          level: 2 },
    ],
  },
  {
    category: 'Practices',
    items: [
      { name: 'Full-Stack Dev',     level: 4 },
      { name: 'Technical Writing',  level: 4 },
      { name: 'CI/CD Pipelines',    level: 3 },
      { name: 'System Design',      level: 3 },
      { name: 'CAD / 3D Printing',  level: 3 },
      { name: 'Agile',              level: 3 },
    ],
  },
];

const timeline = [
  {
    period: '2026 - Present',
    role: 'Founding Engineer',
    org: 'Uplane',
    description: 'Joined as a founding engineer to build and improve full-stack applications for an AI system that generates, tests, and optimizes advertising campaigns automatically.',
    type: 'work',
  },
  {
    period: '2024 - 2026',
    role: 'Lead Software Engineer',
    org: 'Templar Screens',
    description: 'Built a full-stack quoting SaaS app with React and Flask; over $8M ordered across 800+ dealers. Led architecture for multiple systems including a production flow system for 30+ staff. Set up CI/CD pipelines and integrated 15+ third-party services (Stripe, NetSuite, QuickBooks).',
    type: 'work',
  },
  {
    period: '2022 - 2024',
    role: 'B.S. Computer Science',
    org: 'University of Washington',
    description: "Completed Bachelor's in CS with a 3.9 GPA. Placed first in the Puget Sound Programming Competition (Spring 2024). Worked on various full-stack and systems projects deepening core CS concepts.",
    type: 'edu',
  },
  {
    period: '2021 - Present',
    role: 'Founding Software Engineer',
    org: 'ZeroToil',
    description: 'Mentored a development team of 4, shipping high-quality code across major marketplaces with 100,000+ downloads. Grew support community to 1,000+ members and maintained a documentation site using Markdown, Jekyll, and GitHub Pages.',
    type: 'work',
  },
  {
    period: '2020 - 2022',
    role: 'Associate Degree',
    org: 'Green River College',
    description: "Earned Associate Degree through Running Start, managing full-time college alongside high school. Graduated with a 4.0 GPA, Phi Theta Kappa honors.",
    type: 'edu',
  },
  {
    period: '2018 - 2022',
    role: 'High School Graduate',
    org: 'Kent Lake High School',
    description: "Graduated as valedictorian with a 4.0 GPA, earning the Seal of Biliteracy and consistent placement on Honors and Dean's Lists.",
    type: 'edu',
  },
];

function SkillDots({ level }) {
  return (
    <Box sx={{ display: 'flex', gap: '3px', alignItems: 'center', flexShrink: 0 }}>
      {Array.from({ length: 5 }).map((_, i) => (
        <Box
          key={i}
          sx={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            background: i < level ? '#F5F5F7' : 'rgba(255,255,255,0.12)',
            transition: 'background 0.2s ease',
          }}
        />
      ))}
    </Box>
  );
}

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] },
});

export default function Skills() {
  return (
    <Container maxWidth="lg" sx={{ mt: { xs: 8, md: 12 }, mb: 14 }}>

      {/* ── Skills section ──────────────────────────────────────── */}
      <Box sx={{ mb: 14 }}>
        <motion.div {...fadeUp()}>
          <Box sx={{ textAlign: 'center', mb: 10 }}>
            <Typography sx={{
              fontSize: '0.72rem',
              fontWeight: 500,
              color: '#2997FF',
              textTransform: 'uppercase',
              letterSpacing: '0.16em',
              mb: 1.5,
            }}>
              Capabilities
            </Typography>
            <Typography variant="h2" sx={{ mb: 2 }}>
              Technical Skills
            </Typography>
            <Typography variant="body1" sx={{ maxWidth: 460, mx: 'auto' }}>
              A range of languages, frameworks, and tools I've worked with across professional and personal projects.
            </Typography>
          </Box>
        </motion.div>

        <Grid container spacing={{ xs: 6, md: 4 }}>
          {skills.map((cat, i) => (
            <Grid item xs={12} sm={6} md={3} key={cat.category}>
              <motion.div {...fadeUp(i * 0.08)}>
                <Typography sx={{
                  fontSize: '0.68rem',
                  fontWeight: 500,
                  color: 'rgba(245,245,247,0.35)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.14em',
                  mb: 2.5,
                }}>
                  {cat.category}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.4 }}>
                  {cat.items.map((skill) => (
                    <Box key={skill.name} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5 }}>
                      <Typography sx={{
                        color: 'rgba(245,245,247,0.75)',
                        fontSize: '0.95rem',
                        letterSpacing: '-0.01em',
                        lineHeight: 1.4,
                      }}>
                        {skill.name}
                      </Typography>
                      <SkillDots level={skill.level} />
                    </Box>
                  ))}
                </Box>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* ── Divider ──────────────────────────────────────────────── */}
      <Box sx={{ height: '1px', background: 'rgba(255,255,255,0.08)', mb: 14 }} />

      {/* ── Journey section ──────────────────────────────────────── */}
      <motion.div {...fadeUp(0.1)}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: { md: 'flex-end' }, justifyContent: 'space-between', mb: 10, gap: 4 }}>
          <Box>
            <Typography sx={{
              fontSize: '0.72rem',
              fontWeight: 500,
              color: '#2997FF',
              textTransform: 'uppercase',
              letterSpacing: '0.16em',
              mb: 1.5,
            }}>
              Career
            </Typography>
            <Typography variant="h2">
              Professional Journey
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<DownloadIcon sx={{ fontSize: '1rem' }} />}
            href="/resume/Michael_Yarmoshik_Resume.pdf"
            target="_blank"
            sx={{ alignSelf: { xs: 'flex-start', md: 'center' }, flexShrink: 0 }}
          >
            Download Resume
          </Button>
        </Box>
      </motion.div>

      {/* ── Timeline ─────────────────────────────────────────────── */}
      <Box sx={{ position: 'relative', pl: { xs: 3, md: 5 } }}>

        {/* Vertical track */}
        <Box sx={{
          position: 'absolute',
          left: 0,
          top: 6,
          bottom: 6,
          width: '1px',
          background: 'rgba(255,255,255,0.08)',
        }} />

        {timeline.map((item, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, x: -12 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.6, delay: i * 0.07, ease: [0.22, 1, 0.36, 1] }}
          >
            <Box sx={{ position: 'relative', mb: i < timeline.length - 1 ? 8 : 0 }}>

              {/* Timeline dot */}
              <Box sx={{
                position: 'absolute',
                left: { xs: -3 - 12, md: -5 - 12 },
                top: 5,
                width: 9,
                height: 9,
                borderRadius: '50%',
                background: item.type === 'work' ? '#2997FF' : 'rgba(255,255,255,0.18)',
                border: item.type === 'work' ? 'none' : '1px solid rgba(255,255,255,0.2)',
                boxShadow: item.type === 'work' ? '0 0 8px rgba(41,151,255,0.5)' : 'none',
              }} />

              {/* Period */}
              <Typography sx={{
                fontSize: '0.78rem',
                fontWeight: 500,
                color: item.type === 'work' ? '#2997FF' : 'rgba(245,245,247,0.38)',
                letterSpacing: '-0.01em',
                mb: 0.6,
              }}>
                {item.period}
              </Typography>

              {/* Role */}
              <Typography sx={{
                color: '#F5F5F7',
                fontWeight: 600,
                fontSize: '1.12rem',
                letterSpacing: '-0.02em',
                mb: 0.3,
              }}>
                {item.role}
              </Typography>

              {/* Organisation */}
              <Typography sx={{
                color: 'rgba(245,245,247,0.45)',
                fontSize: '0.88rem',
                mb: 1.5,
                letterSpacing: '-0.01em',
              }}>
                {item.org}
              </Typography>

              {/* Description */}
              <Typography sx={{
                color: 'rgba(245,245,247,0.58)',
                fontSize: '0.95rem',
                lineHeight: 1.75,
                maxWidth: 600,
              }}>
                {item.description}
              </Typography>
            </Box>
          </motion.div>
        ))}
      </Box>
    </Container>
  );
}
